<template>
  <div class="bill-list-container">
        <!-- ── Premium Table ─────────────────────── -->
        <PremiumTable
          title="Bill Master"
          subtitle="Manage society-wide recurring and one-time bill templates"
          add-label="Add"
          :show-view="true"
          :show-delete="true"
          @add="$router.push('/societyadmin/bills/add')"
          @view="$router.push('/societyadmin/bills/view/' + $event.id)"
          @delete="confirmDelete($event.id)"
          :columns="columns"
          :items="bills"
          :loading="loading"
          :total-items="totalRecords"
          :page.sync="page"
          :items-per-page.sync="itemsPerPage"
          :searchable="true"
          :search="search"
          @search="onSearch"
        >
          <!-- Header Extra: Type Filter -->
          <template #header-extra>
            <div class="header-controls">
              <!-- Bill Type Filter -->
              <FilterDropdown
                v-model="selectedType"
                :options="typeOptions"
                item-text="label"
                item-value="value"
                placeholder="All Types"
                all-label="All"
                @change="onTypeChange"
              />

              <!-- Month Picker (commented out) -->
              <!--
              <PremiumDatePicker
                v-model="date"
                type="month"
                placeholder="Select Month"
                @change="getBills"
              />
              -->
            </div>
          </template>

          <!-- Custom Body Columns -->
          <template #col-title="{ item }">
            <v-tooltip
              bottom
              nudge-bottom="4"
              color="primary"
              max-width="400"
              :disabled="
                !(item.title && item.title.length > 30) &&
                !(item.description && item.description.length > 30)
              "
            >
              <template v-slot:activator="{ on, attrs }">
                <div class="bill-title-cell" v-bind="attrs" v-on="on">
                  <span class="bill-title-text">{{ item.title }}</span>
                  <span class="bill-desc-text" v-if="item.description">{{
                    item.description
                  }}</span>
                </div>
              </template>
              <div>
                <div style="font-weight: 700; margin-bottom: 2px">
                  {{ item.title }}
                </div>
                <div
                  v-if="item.description"
                  style="font-size: 12px; opacity: 0.85"
                >
                  {{ item.description }}
                </div>
              </div>
            </v-tooltip>
          </template>

          <template #col-type="{ item }">
            <span class="type-badge" :class="'type-' + item.type">
              {{ types[item.type] || "Unknown" }}
            </span>
          </template>

          <template #col-amount="{ item }">
            <span class="amount-text"
              >₹{{ Number(item.amount).toLocaleString() }}</span
            >
          </template>

          <template #col-created_on="{ item }">
            <span class="date-text">{{ formatBillCreatedDate(item) }}</span>
          </template>

          <template #col-due_days="{ item }">
            <span class="due-text" :class="{ 'due-overdue': isDueOverdue(item) }">
              {{ formatBillDueDate(item) }}
            </span>
          </template>

          <!-- Custom Actions: hide Edit if one-time and overdue -->
          <template #actions="{ item }">
            <div style="display:flex;align-items:center;justify-content:flex-end;gap:6px">
              <ActionButtons
                :show-view="true"
                :show-edit="!isOneTimeOverdue(item)"
                :show-delete="true"
                @view="$router.push('/societyadmin/bills/view/' + item.id)"
                @edit="$router.push('/societyadmin/bills/edit/' + item.id)"
                @delete="confirmDelete(item.id)"
              />
            </div>
          </template>
        </PremiumTable>

    <!-- ── Confirm Deletion ─────────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Bill Master"
      message="Are you sure you want to delete this bill?"
      confirm-text="Yes, Delete Bill"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteBill"
    />

    <!-- ── Export Confirmation ──────────────── -->
    <ConfirmModal
      v-model="exportDialog"
      type="primary"
      :btnColor="brandColor"
      title="Export to Excel"
      message="Are you sure you want to export the bill master list to an Excel file?"
      confirm-text="Yes, Export"
      @confirm="
        exportToExcel();
        exportDialog = false;
      "
    />
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import moment from "moment";
import PremiumTable from "../../common/PremiumTable.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import FilterDropdown from "../../common/FilterDropdown.vue";
import ActionButtons from "../../common/ActionButtons.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: "BillList",
  mixins: [filterMixin],
  components: {
    PremiumTable,
    ConfirmModal,
    FilterDropdown,
    ActionButtons,
  },
  data() {
    return {
      moment: moment,
      bills: [],
      loading: false,
      deleteLoading: false,
      deleteDialog: false,
      delete_id: null,
      search: "",
      page: 1,
      itemsPerPage: 10,
      totalRecords: 0,
      menu: false,
      date: null,
      user: {},
      exportDialog: false,
      selectedType: null,
      typeOptions: [
        { label: "One Time", value: 1 },
        { label: "Monthly",  value: 2 },
        { label: "Yearly",   value: 3 },
      ],
      summary: {
        total_billed: 0,
        collected: 0,
        pending: 0,
        overdue: 0,
        pct: 0,
        pending_count: 0,
        overdue_count: 0,
      },
      types: {
        1: "One Time",
        2: "Monthly",
        3: "Yearly",
      },
    };
  },
  computed: {
    ...mapState("theme", ["isDark", "primaryColor", "buttonColor"]),
    brandColor() {
      return this.buttonColor || this.primaryColor || "#30384d";
    },
    columns() {
      return [
        { key: "title", label: "Bill Details", width: "250px" },
        { key: "type", label: "Type" },
        { key: "amount", label: "Owner Amt" },
        { key: "amount_for_rental", label: "Tenant Amt" },
        { key: "created_on", label: "Created" },
        { key: "due_days", label: "Due Date" },
      ];
    },
    dateLabel() {
      return this.date
        ? moment(this.date).format("MMM YYYY")
        : moment().format("MMM YYYY");
    },
  },
  watch: {
    // date() {
    //   this.page = 1;
    //   this.getBills();
    // },
    page() {
      this.getBills();
    },
    itemsPerPage() {
      if (this.page === 1) {
        this.getBills();
      } else {
        this.page = 1;
      }
    },
    search() {
      if (this.page === 1) {
        this.getBills();
      } else {
        this.page = 1;
      }
    },
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    onSearch(val) {
      this.search = val;
    },
    onTypeChange(val) {
      this.selectedType = val;
      if (this.page === 1) {
        this.getBills();
      } else {
        this.page = 1;
      }
    },
    // Returns true if bill is One Time AND its due date has passed
    isOneTimeOverdue(item) {
      if (item.type != 1) return false;
      if (!item.one_time_date) return false;
      return moment().isSameOrAfter(Number(item.one_time_date), 'day');
    },
    isDueOverdue(item) {
      const due = this.calculateDueDate(item.one_time_date, item.due_days);
      return due ? moment().isAfter(due, 'day') : false;
    },
    getBills() {
      this.loading = true;
      let url = `societyadmin/bills?society_id=${this.user.id}&page=${this.page}&perPage=${this.itemsPerPage}`;
      // Month filter commented out:
      // if (this.date) { const month = ...; const year = ...; url += `&month=${month}&year=${year}`; }
      if (this.selectedType) url += `&type=${this.selectedType}`;
      if (this.search) url += `&search=${this.search}`;

      this.$axios
        .get(url)
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            // Priority: data.bills.data (paginated) > data.bills (array)
            this.bills = data.bills.data || data.bills || [];
            
            // Priority: data.bills.total (nested) > data.total (top-level)
            this.totalRecords = (data.bills && data.bills.total) || data.total || data.Total || 0;

            if (data.summary) {
              this.summary = data.summary;
            }
          } else {
            this.showError(data.message);
            if (data.type === "invalid") {
              localStorage.removeItem("user");
              this.$router.push("/societyadmin/login");
            }
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
    formatCurrency(val) {
      if (!val || val == 0) return "₹0";
      if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
      if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
      return `₹${Number(val).toLocaleString()}`;
    },
    formatBillCreatedDate(item) {
      const dateValue = item.one_time_date || item.created_on || item.created_at;
      if (!dateValue) return "N/A";
      const date = isNaN(dateValue) ? moment(dateValue) : moment(Number(dateValue));
      return date.isValid() ? date.format("DD MMM, YYYY") : "N/A";
    },
    formatBillDueDate(item) {
      const due = this.calculateDueDate(item.one_time_date, item.due_days);
      return due && due.isValid() ? due.format("DD MMM, YYYY") : "N/A";
    },
    exportToExcel() {
      let month, year;
      if (this.date) {
        month = moment(this.date, "YYYY-MM").get("month") + 1;
        year = moment(this.date, "YYYY-MM").get("year");
      }
      this.$axios
        .get("societyadmin/export/bills/master", {
          params: {
            society_id: this.user.id,
            month,
            year,
            search: this.search,
          },
          responseType: "blob",
        })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `Bill_Master_${moment().format("YYYY-MM-DD")}.xlsx`,
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(() => {
          this.showError("Failed to export data");
        });
    },
    confirmDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },
    deleteBill() {
      this.deleteLoading = true;
      this.$axios
        .delete("societyadmin/bills/" + this.delete_id)
        .then((response) => {
          this.deleteLoading = false;
          const data = response.data;
          if (data.status) {
            this.deleteDialog = false;
            this.getBills();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.deleteLoading = false;
        });
    },
    getFormattedDate(date) {
      return date ? moment(date).format("DD MMM, YYYY · hh:mm A") : "N/A";
    },
    calculateDueDate(one_time_date, due_days) {
      if (!one_time_date || due_days === undefined || due_days === null) {
        return null;
      }
      const value = isNaN(one_time_date) ? one_time_date : Number(one_time_date);
      const date = moment(value);
      const days = Number(due_days);
      if (!date.isValid() || isNaN(days)) {
        return null;
      }
      return date.clone().add(days, "days");
    },
    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },
  },
  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};
    
    // Initialize persistent filters for this module
    this.initFilters('bills');

    if (!this.date) {
      this.date = moment().format("YYYY-MM");
    }
    
    this.getBills();
  },
};
</script>

<style scoped>
.bill-list-container {
  padding: 24px;
}

/* Stat Cards Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

@media (max-width: 600px) {
  .header-controls {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 8px;
  }
  .header-controls >>> .v-input,
  .header-controls >>> .premium-datepicker-wrap {
    width: 100% !important;
    min-width: unset !important;
  }
  .pt-export-btn {
    width: 100% !important;
    height: 42px !important;
    border-radius: 8px !important;
  }
}

.custom-date-trigger {
  display: flex;
  align-items: center;
  height: 38px;
  padding: 0 16px;
  background: var(--surface2);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 700;
  color: var(--tx2);
  cursor: pointer;
  transition: all 0.2s;
  min-width: 180px;
}
.custom-date-trigger:hover {
  border-color: var(--brand);
  transform: translateY(-1px);
}

.icon-action-btn {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--surface2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tx2);
  cursor: pointer;
  transition: all 0.2s;
}
.icon-action-btn:hover {
  border-color: var(--brand);
  color: var(--brand);
  transform: translateY(-1px);
}

/* ── Cells ── */
.bill-title-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
}
.bill-title-text {
  font-size: 14px;
  font-weight: 700;
  color: var(--tx);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: default;
}
.bill-desc-text {
  font-size: 11.5px;
  color: var(--tx3);
  font-weight: 500;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: default;
}

/* ── Standard Chip ── */
.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px 3px 7px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  white-space: nowrap;
}
.type-badge::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.type-1 {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}
.type-1::before { background: #3b82f6; }
.type-2 {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}
.type-2::before { background: #10b981; }
.type-3 {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}
.type-3::before { background: #f59e0b; }

.amount-text {
  font-weight: 800;
  color: var(--tx);
  font-family: "JetBrains Mono", monospace;
  font-size: 14px;
}
.date-text {
  font-size: 13px;
  color: var(--tx3);
  font-weight: 600;
}
.due-text {
  font-size: 13px;
  font-weight: 700;
  color: #ef4444;
}
.due-overdue {
  color: #ef4444 !important;
}
</style>
